import React, { useEffect, useState } from "react";
import {
  fetchTopicLinks, createTopicLink, deleteTopicLink,
  fetchContentBlocks, fetchPracticeSets, fetchQuizzes,
} from "./adminContentService";

const REF_TYPES = ["CONTENT", "PRACTICE", "QUIZ"];

const typeBadgeStyle = (type) => ({
  padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
  background: type === "CONTENT" ? "#e3f2fd" : type === "PRACTICE" ? "#e8f5e9" : "#fff3e0",
  color: type === "CONTENT" ? "#1565c0" : type === "PRACTICE" ? "#2e7d32" : "#e65100",
});

/**
 * Props:
 *  topicType: "GRAMMAR_SUB_TOPIC" | "READING_PASSAGE" | "GD_TOPIC"
 *  topicId: number
 */
const TopicLinksPanel = ({ topicType, topicId }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [refType, setRefType] = useState("CONTENT");
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);

  const loadLinks = async () => {
    if (!topicId) return;
    setLoading(true);
    const res = await fetchTopicLinks(topicType, topicId);
    if (res.status === 200) setLinks(Array.isArray(res.data) ? res.data : []);
    setLoading(false);
  };

  useEffect(() => { loadLinks(); }, [topicId, topicType]); // eslint-disable-line

  useEffect(() => {
    if (!showModal) return;
    setOptions([]);
    setSelectedId("");
    const t = setTimeout(async () => {
      const params = { page: 0, size: 30, status: "ACTIVE" };
      if (search) params.search = search;
      let res;
      if (refType === "CONTENT") res = await fetchContentBlocks(params);
      else if (refType === "PRACTICE") res = await fetchPracticeSets(params);
      else res = await fetchQuizzes(params);
      if (res.status === 200) {
        const d = res.data;
        setOptions(Array.isArray(d) ? d : d.content ?? []);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [showModal, refType, search]);

  const handleAdd = async () => {
    if (!selectedId) return alert("Please select an item");
    setSaving(true);
    const res = await createTopicLink({ topicType, topicId: Number(topicId), refType, refId: Number(selectedId), sortOrder: Number(sortOrder) });
    setSaving(false);
    if (res.status === 200 || res.status === 201) {
      setShowModal(false);
      setSearch("");
      setSelectedId("");
      setSortOrder(links.length);
      loadLinks();
    } else {
      alert(res.message || "Failed to add link");
    }
  };

  const handleRemove = async (linkId) => {
    if (!window.confirm("Remove this link?")) return;
    const res = await deleteTopicLink(linkId);
    if (res.status === 200 || res.status === 204) loadLinks();
    else alert(res.message || "Failed to remove link");
  };

  const inp = { padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ marginTop: 32, background: "#f9f9f9", borderRadius: 10, padding: 20, border: "1px solid #e0e0e0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>🔗 Linked Content / Practice / Quiz</h3>
        <button
          onClick={() => { setSortOrder(links.length); setShowModal(true); }}
          style={{ padding: "6px 14px", background: "#019095", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
          + Add Link
        </button>
      </div>

      {loading ? <p style={{ color: "#888", fontSize: 13 }}>Loading…</p> : links.length === 0 ? (
        <p style={{ color: "#aaa", fontSize: 13 }}>No linked items yet.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Type</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Title</th>
              <th style={{ textAlign: "center", padding: "6px 8px" }}>Sort</th>
              <th style={{ padding: "6px 8px" }}></th>
            </tr>
          </thead>
          <tbody>
            {links.map((lk) => {
              const title = lk.refTitle || lk.content?.title || lk.practice?.title || lk.quiz?.title || `ID ${lk.refId}`;
              return (
                <tr key={lk.linkId} style={{ borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "8px" }}><span style={typeBadgeStyle(lk.refType)}>{lk.refType}</span></td>
                  <td style={{ padding: "8px", fontWeight: 500 }}>{title}</td>
                  <td style={{ padding: "8px", textAlign: "center", color: "#888" }}>{lk.sortOrder}</td>
                  <td style={{ padding: "8px", textAlign: "right" }}>
                    <button onClick={() => handleRemove(lk.linkId)}
                      style={{ color: "#e53935", background: "none", border: "none", cursor: "pointer", fontSize: 13 }}>Remove</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Add Link Modal */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={() => setShowModal(false)}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: 480, maxHeight: "80vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <strong style={{ fontSize: 15 }}>Add Link</strong>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer" }}>×</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Ref Type</label>
                <select style={inp} value={refType} onChange={(e) => { setRefType(e.target.value); setSearch(""); setSelectedId(""); }}>
                  {REF_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Search</label>
                <input style={inp} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title…" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Select Item</label>
                <select style={{ ...inp, minHeight: 120 }} size={5} value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
                  <option value="">— pick one —</option>
                  {options.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Sort Order</label>
                <input type="number" style={inp} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
              </div>
              <button onClick={handleAdd} disabled={saving || !selectedId}
                style={{ padding: "9px 20px", background: "#019095", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, opacity: selectedId ? 1 : 0.5 }}>
                {saving ? "Adding…" : "Add Link"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicLinksPanel;
